import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, ChevronRight, Eye, EyeOff, Loader2, Lock, MessageSquare, Plus, Power, Trophy, Unlock } from "lucide-react";
import useAdminAuth, { adminAuthHeader } from "@/hooks/useAdminAuth";
import TemplateCard from "./admin/messages/TemplateCard";
import WhatsAppPreview from "./admin/messages/WhatsAppPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/messaggi — Phase 6 editor (DB-driven, full CRUD).
 *
 * What you can do live (no redeploy):
 *   · toggle a rule ON/OFF
 *   · add a new sub-message inside an existing variant
 *   · add a new variant for a rule (clone of an existing one)
 *   · delete a sub-message
 *   · edit copy, delay (timer), link kind, enabled flag per sub-message
 *
 * What you CANNOT do here (requires code change in followup_engine.py):
 *   · add a brand-new rule (R12, R13…) — trigger logic is engine-side
 *   · change the trigger event of a rule (E1..E5)
 */
export default function AdminMessagesPage() {
  const { authed, verifying } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [rules, setRules] = useState([]);
  const [abStats, setAbStats] = useState({});  // { R1: {A:{rate_pct,converted,assigned}, ...}, R2: {...} }
  const [loading, setLoading] = useState(true);
  const [openRules, setOpenRules] = useState(() => new Set(["R1", "R2"]));
  const [previewRules, setPreviewRules] = useState(() => new Set(["R1", "R2", "R7"]));
  const [busyRule, setBusyRule] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tplRes, autoRes] = await Promise.all([
        axios.get(`${API}/admin/message-templates`, { headers: adminAuthHeader() }),
        axios.get(`${API}/admin/automation/overview`, { headers: adminAuthHeader() }).catch(() => ({ data: {} })),
      ]);
      setItems(tplRes.data?.items || []);
      setRules(tplRes.data?.rules || []);
      // Merge R1/R2 A/B stats into a lookup keyed by rule → variant for the badges
      setAbStats({
        R1: autoRes.data?.ab_test_r1 || {},
        R2: autoRes.data?.ab_test_r2 || {},
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!verifying && authed) load(); }, [verifying, authed]);

  // Pivot items by rule → variant → sorted by sequence_index
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((t) => {
      if (!t.rule || t.rule === "legacy" || t.rule === "—") return;  // hide legacy from main view
      g[t.rule] = g[t.rule] || {};
      const vk = t.variant || "_";
      g[t.rule][vk] = g[t.rule][vk] || [];
      g[t.rule][vk].push(t);
    });
    Object.values(g).forEach((byVariant) => {
      Object.values(byVariant).forEach((arr) => arr.sort((a, b) => (a.sequence_index ?? 0) - (b.sequence_index ?? 0)));
    });
    return g;
  }, [items]);

  const rulesByCode = useMemo(() => {
    const m = {};
    rules.forEach((r) => { m[r.rule_code] = r; });
    return m;
  }, [rules]);

  const ruleCodesSorted = useMemo(
    () => Object.keys(grouped).sort((a, b) => {
      const na = parseInt(a.replace("R", ""), 10);
      const nb = parseInt(b.replace("R", ""), 10);
      return (Number.isFinite(na) ? na : 999) - (Number.isFinite(nb) ? nb : 999);
    }),
    [grouped],
  );

  const byTag = useMemo(() => {
    const m = {};
    items.forEach((t) => { m[t.tag] = t; });
    return m;
  }, [items]);

  const toggleRuleOpen = (rule) => {
    setOpenRules((prev) => { const n = new Set(prev); n.has(rule) ? n.delete(rule) : n.add(rule); return n; });
  };
  const togglePreview = (rule) => {
    setPreviewRules((prev) => { const n = new Set(prev); n.has(rule) ? n.delete(rule) : n.add(rule); return n; });
  };

  const toggleRuleEnabled = async (rule) => {
    const cur = rulesByCode[rule]?.enabled !== false;
    setBusyRule(rule);
    try {
      await axios.patch(`${API}/admin/rule-configs/${rule}`,
        { enabled: !cur }, { headers: adminAuthHeader() });
      toast.success(`Regola ${rule}: ${!cur ? "ATTIVATA" : "DISATTIVATA"}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore toggle");
    } finally {
      setBusyRule(null);
    }
  };

  // Pin an A/B variant as the winner. Pass `null` to release the lock and
  // resume the random split. When locked, every NEW lead assigned to this
  // rule is bucketed straight to `variant` (users with an existing sticky
  // assignment are NOT re-assigned).
  const lockVariant = async (rule, variant) => {
    const humanAction = variant
      ? `Fissare la variante ${variant} come vincente per ${rule}?\n\n` +
        `Tutti i NUOVI lead che triggerano ${rule} riceveranno la variante ${variant}. ` +
        "L'A/B split viene messo in pausa (i lead già assegnati mantengono la loro variante)."
      : `Sbloccare la variante fissata per ${rule}?\n\n` +
        "L'A/B split ripartirà con l'assegnazione casuale sui nuovi lead.";
    if (!window.confirm(humanAction)) return;
    setBusyRule(rule);
    try {
      await axios.patch(`${API}/admin/rule-configs/${rule}`,
        { locked_variant: variant }, { headers: adminAuthHeader() });
      toast.success(variant
        ? `${rule}: variante ${variant} fissata come vincente`
        : `${rule}: A/B split riattivato`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore lock variante");
    } finally {
      setBusyRule(null);
    }
  };

  // Add a new sub-message at the end of a (rule, variant) sequence
  const addSubMessage = async (rule, variant, currentArr) => {
    const lastIdx = currentArr.length ? Math.max(...currentArr.map((t) => t.sequence_index ?? 0)) : -1;
    const lastDelay = currentArr.length ? (currentArr[currentArr.length - 1].delay_seconds ?? 0) : 0;
    const newTag = window.prompt(
      `Tag univoco del nuovo sotto-messaggio per ${rule}${variant ? "·" + variant : ""}\n(es. msg_custom_${rule.toLowerCase()}_${lastIdx + 2})`,
      `msg_custom_${rule.toLowerCase()}_${lastIdx + 2}`,
    );
    if (!newTag) return;
    try {
      await axios.post(`${API}/admin/message-templates`, {
        tag: newTag.trim(),
        rule,
        variant: variant || null,
        sequence_index: lastIdx + 1,
        delay_seconds: lastDelay + 10,
        copy: "(nuovo sotto-messaggio — modifica il copy)",
        link: null,
      }, { headers: adminAuthHeader() });
      toast.success(`Aggiunto ${newTag}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione");
    }
  };

  // Clone an existing variant into a new variant letter (e.g. R1·A → R1·D)
  const addVariant = async (rule, sourceVariantArr, currentVariants) => {
    const used = new Set(currentVariants.filter((v) => v !== "_"));
    const next = ["A", "B", "C", "D", "E", "F"].find((c) => !used.has(c));
    if (!next) { toast.error("Massimo 6 varianti per regola"); return; }
    const proceed = window.confirm(`Clonare la prima variante in una nuova variante ${next} per ${rule}?`);
    if (!proceed) return;
    try {
      for (const t of sourceVariantArr) {
        const newTag = `${t.tag}_${next.toLowerCase()}clone_${Date.now()}`;
        await axios.post(`${API}/admin/message-templates`, {
          tag: newTag,
          rule,
          variant: next,
          sequence_index: t.sequence_index,
          delay_seconds: t.delay_seconds,
          copy: t.copy,
          link: t.link,
        }, { headers: adminAuthHeader() });
      }
      toast.success(`Variante ${next} clonata`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore clonazione");
    }
  };

  if (verifying) return <main className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></main>;
  if (!authed) return <main className="p-8"><Link to="/admin/dashboard" className="underline">vai al login</Link></main>;

  return (
    <main className="min-h-screen" style={{ background: "var(--np-tertiary)" }} data-testid="admin-messages-page">
      <header className="border-b sticky top-0 z-10" style={{ borderColor: "#dbe3ec", background: "#fff" }}>
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--np-primary)" }}>
            <ArrowLeft size={15} /> Dashboard
          </Link>
          <h1 className="text-lg font-semibold flex items-center gap-2" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>
            <MessageSquare size={17} style={{ color: "var(--np-secondary)" }} />
            Editor messaggi · CRUD live
          </h1>
          <div style={{ width: 80 }} />
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" /></div>
        ) : (
          ruleCodesSorted.map((rule) => {
            const ruleCfg = rulesByCode[rule];
            const variantsMap = grouped[rule] || {};
            const variantKeys = Object.keys(variantsMap).sort((a, b) => a.localeCompare(b));
            const isOpen = openRules.has(rule);
            const showPreview = previewRules.has(rule);
            const ruleEnabled = ruleCfg?.enabled !== false;

            return (
              <section
                key={rule}
                className="rounded-2xl border bg-white overflow-hidden"
                style={{ borderColor: ruleEnabled ? "#dbe3ec" : "#fca5a5", opacity: ruleEnabled ? 1 : 0.85 }}
                data-testid={`rule-group-${rule}`}
              >
                <div className="w-full flex items-center justify-between gap-3 px-5 py-4">
                  <button
                    type="button"
                    onClick={() => toggleRuleOpen(rule)}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left"
                    data-testid={`rule-toggle-${rule}`}
                  >
                    {isOpen ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
                    <span className="text-[11px] font-bold uppercase tracking-[0.22em] px-2 py-1 rounded" style={{ background: "rgba(213,93,57,0.1)", color: "var(--np-secondary)" }}>
                      {rule}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-[14px] font-semibold truncate" style={{ color: "var(--np-primary)" }}>
                        {ruleCfg?.title || rule}
                      </h2>
                      <p className="text-[11px] truncate" style={{ color: "var(--np-muted)" }}>
                        {ruleCfg?.trigger_label || "—"}
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleRuleEnabled(rule)}
                    disabled={busyRule === rule}
                    data-testid={`rule-toggle-enabled-${rule}`}
                    title={ruleEnabled ? "Disattiva regola" : "Attiva regola"}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
                    style={{
                      background: ruleEnabled ? "#fff" : "#fee2e2",
                      borderColor: ruleEnabled ? "#16a34a" : "#dc2626",
                      color: ruleEnabled ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {busyRule === rule ? <Loader2 size={11} className="animate-spin" /> : <Power size={11} />}
                    {ruleEnabled ? "ATTIVA" : "DISATTIVATA"}
                  </button>
                </div>

                {isOpen && (
                  <div className="border-t px-5 py-5" style={{ borderColor: "#eef2f7", background: "#fafbfd" }}>
                    <div className="mb-4 flex justify-between items-center flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => togglePreview(rule)}
                        data-testid={`preview-toggle-${rule}`}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
                        style={{ borderColor: "#cbd5e1", color: "#475569", background: "#fff" }}
                      >
                        {showPreview ? <><EyeOff size={11} /> Nascondi anteprima</> : <><Eye size={11} /> Mostra anteprima WhatsApp</>}
                      </button>
                      {/* Add variant button — only if at least one variant already exists */}
                      {variantKeys.some((v) => v !== "_") && (
                        <button
                          type="button"
                          onClick={() => addVariant(rule, variantsMap[variantKeys[0]], variantKeys)}
                          data-testid={`rule-add-variant-${rule}`}
                          className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white"
                          style={{ background: "var(--np-secondary)" }}
                        >
                          <Plus size={11} /> Clona variante
                        </button>
                      )}
                    </div>

                    <div className={`grid gap-5 ${variantKeys.length >= 3 ? "lg:grid-cols-3" : variantKeys.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
                      {variantKeys.map((vk) => {
                        const arr = variantsMap[vk];
                        const locked = ruleCfg?.locked_variant || null;
                        const isLockedHere = locked === vk;
                        const isLockedElsewhere = locked && locked !== vk;
                        const stat = vk !== "_" ? (abStats[rule] || {})[vk] : null;
                        return (
                          <div key={vk} className="space-y-3" data-testid={`variant-col-${rule}-${vk}`} style={{ opacity: isLockedElsewhere ? 0.55 : 1 }}>
                            {vk !== "_" && (
                              <div className="pb-2 border-b" style={{ borderColor: isLockedHere ? "#f59e0b" : "#e2e8f0" }}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span
                                      className="inline-flex items-center justify-center w-7 h-7 rounded-full font-bold text-[12px]"
                                      style={{ background: isLockedHere ? "#f59e0b" : "var(--np-secondary)", color: "#fff" }}
                                    >
                                      {vk}
                                    </span>
                                    <p className="text-[12px] font-semibold" style={{ color: "var(--np-primary)" }}>
                                      Variante {vk}
                                    </p>
                                    {isLockedHere && (
                                      <span
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                        style={{ background: "#fef3c7", color: "#92400e" }}
                                        data-testid={`variant-locked-badge-${rule}-${vk}`}
                                      >
                                        <Trophy size={10} /> Vincente
                                      </span>
                                    )}
                                    {stat && Number.isFinite(stat.rate_pct) && stat.assigned > 0 && (
                                      <span
                                        className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                                        style={{ background: "#eff6ff", color: "#1e40af" }}
                                        title={`${stat.converted} conv su ${stat.assigned} assegnati`}
                                      >
                                        {stat.rate_pct}%
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px]" style={{ color: "var(--np-muted)" }}>{arr.length} sotto-msg</span>
                                    {isLockedHere ? (
                                      <button
                                        type="button"
                                        onClick={() => lockVariant(rule, null)}
                                        disabled={busyRule === rule}
                                        data-testid={`variant-unlock-${rule}-${vk}`}
                                        title="Sblocca — riattiva l'A/B split casuale"
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border"
                                        style={{ borderColor: "#f59e0b", color: "#92400e", background: "#fff" }}
                                      >
                                        {busyRule === rule ? <Loader2 size={10} className="animate-spin" /> : <Unlock size={10} />}
                                        Sblocca
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => lockVariant(rule, vk)}
                                        disabled={busyRule === rule}
                                        data-testid={`variant-lock-${rule}-${vk}`}
                                        title={locked
                                          ? `Sposta il vincente da ${locked} a ${vk}`
                                          : "Fissa questa variante come vincente — tutti i nuovi lead la riceveranno"}
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border"
                                        style={{ borderColor: "#cbd5e1", color: "#475569", background: "#fff" }}
                                      >
                                        {busyRule === rule ? <Loader2 size={10} className="animate-spin" /> : <Lock size={10} />}
                                        Fissa vincente
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                            {showPreview && (
                              <WhatsAppPreview
                                tags={arr.map((t) => t.tag)}
                                delays={arr.map((t) => t.delay_seconds ?? 0)}
                                templates={byTag}
                              />
                            )}
                            <div className="space-y-2.5">
                              {arr.map((t) => (
                                <TemplateCard
                                  key={t.tag} t={t}
                                  onSaved={load}
                                  onDeleted={load}
                                />
                              ))}
                              <button
                                type="button"
                                onClick={() => addSubMessage(rule, vk === "_" ? null : vk, arr)}
                                data-testid={`add-sub-msg-${rule}-${vk}`}
                                className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-2 rounded-md border-2 border-dashed"
                                style={{ borderColor: "#cbd5e1", color: "var(--np-secondary)", background: "transparent" }}
                              >
                                <Plus size={11} /> Aggiungi sotto-messaggio
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </main>
  );
}
